import React, { useEffect, useState, useCallback } from 'react';
import AntToolbar from './AntToolbar';
import AntLocalization from '../utils/AntLocalization';
import AntWidgetSkeleton, { AntWidgetEmpty } from './AntWidgetSkeleton';
import useWidgetFirstLoad from './AntWidgetSkeleton/useWidgetFirstLoad';

export default function (props) {
    var [rows, setRows] = useState([]);
    var [loaded, markLoaded] = useWidgetFirstLoad(Boolean(props.url));

    var load = useCallback(async () => {
        if (props.url) {
            props.API.post(props.url, { ...props.searchConditions }, (resp) => {
                var result = [];
                if (resp && !resp.error) {
                    if (resp.data instanceof Array) result = resp.data;
                    else if (resp.data && resp.data.rows instanceof Array) result = resp.data.rows;
                    else if (resp.data && resp.data.items instanceof Array) result = resp.data.items;
                }
                setRows(result);
                if (props.onEmpty) props.onEmpty(result.length === 0);
                markLoaded();
            });
        }
    }, [props.API, props.url, props.searchConditions, markLoaded]);

    useEffect(() => {
        load();
        var interval = setInterval(load, 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        load();
    }, [props.searchConditions, props.url]);

    useEffect(() => {
        if (props.dataSource && !props.url) setRows(props.dataSource);
    }, [props.dataSource]);

    var formatValue = (val, dataType) => {
        if (val === undefined || val === null) return '0';
        var num = parseFloat(val);
        if (isNaN(num)) return '0';
        if (dataType === 'currency') return AntLocalization.translateCurrency(num);
        if (dataType === 'int') return AntLocalization.translateInteger(num);
        if (dataType === 'float') return AntLocalization.translateDecimal(num);
        return String(val);
    };

    var labelField = props.labelField || 'name';
    var currentField = props.currentField || 'current';
    var targetField = props.targetField || 'target';
    var valueDataType = props.valueDataType || 'currency';

    // Relative mode: when no targetField, compute bars relative to max value
    var maxCurrent = 0;
    if (!props.targetField) {
        rows.forEach((row) => {
            var v = Math.abs(parseFloat(row[currentField]) || 0);
            if (v > maxCurrent) maxCurrent = v;
        });
    }

    var isRelativeMode = !props.targetField;

    var getBarColor = (pct) => {
        if (isRelativeMode) return 'var(--AnteBaseColor1)';
        if (pct >= 90) return '#b34040';
        if (pct >= 70) return '#b8860b';
        return 'var(--AnteBaseColor1)';
    };

    if (!loaded) {
        return (
            <React.Fragment>
                {props.title && <AntToolbar title={props.title} isH6={true} upperCase={true} />}
                <AntWidgetSkeleton variant="list" rows={4} />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {props.title && <AntToolbar title={props.title} isH6={true} upperCase={true} />}
            <div className="ant-widget-progress-list">
                {rows.map((row, idx) => {
                    var current = parseFloat(row[currentField]) || 0;
                    var target = isRelativeMode ? (maxCurrent || 1) : (parseFloat(row[targetField]) || 1);
                    var pct = Math.min((current / target) * 100, 100);
                    var label = row[labelField] || '';
                    if (typeof label === 'object') label = AntLocalization.translateText(label);

                    return (
                        <div key={idx} className="ant-widget-progress-list__item">
                            <div className="ant-widget-progress-list__header">
                                <span className="ant-widget-progress-list__label">{label}</span>
                                {isRelativeMode && <span className="ant-widget-progress-list__value-inline">{formatValue(current, valueDataType)}</span>}
                                {!isRelativeMode && <span className="ant-widget-progress-list__pct">{pct.toFixed(0)}%</span>}
                            </div>
                            {!isRelativeMode && (
                                <div className="ant-widget-progress-list__track">
                                    <div
                                        className="ant-widget-progress-list__fill"
                                        style={{ width: pct + '%', background: row.color || getBarColor(pct) }}
                                    />
                                </div>
                            )}
                            {!isRelativeMode && props.showValues !== false && (
                                <div className="ant-widget-progress-list__values">
                                    <span>{formatValue(current, valueDataType)}</span>
                                    <span className="ant-widget-progress-list__separator">/</span>
                                    <span>{formatValue(target, valueDataType)}</span>
                                </div>
                            )}
                        </div>
                    );
                })}
                {rows.length === 0 && <AntWidgetEmpty />}
            </div>
        </React.Fragment>
    );
}
